import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";

import {
  SettingsOutlinedIcon,
  LogoutIcon,
  Person2OutlinedIcon,
  NotificationsNoneIcon,
} from "../access/icons/Icons";

import {
  Badge,
  Box,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import formatDate from "../components/FormatDate";
import { useNotifications } from "../hooks/useNotifications";

function UserLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const role = "user";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      // token missing -> ensure stale session markers are cleared
      localStorage.removeItem("lastActive");
      localStorage.removeItem("userId");
      localStorage.removeItem("role");
      localStorage.removeItem("isVerified");
      return navigate("/login");
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));

      if (payload.exp) {
        const exp = payload.exp * 1000;
        if (Date.now() > exp) {
          console.log("Token expired");
          localStorage.removeItem("token");
          localStorage.removeItem("role");
          localStorage.removeItem("isVerified");
          localStorage.removeItem("lastActive");
          localStorage.removeItem("userId");
          return navigate("/login");
        }
      }
    } catch (error) {
      console.log("Invalid JWT");
      localStorage.removeItem("token");
      localStorage.removeItem("role");
      localStorage.removeItem("isVerified");
      localStorage.removeItem("lastActive");
      localStorage.removeItem("userId");
      return navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const INACTIVITY_LIMIT = 30 * 60 * 1000;
    let timeoutId;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log("Auto logout due to inactivity");
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("isVerified");
        localStorage.removeItem("lastActive");
        localStorage.removeItem("userId");
        navigate("/login");
      }, INACTIVITY_LIMIT);
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("click", resetTimer);
    window.addEventListener("scroll", resetTimer);

    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("click", resetTimer);
      window.removeEventListener("scroll", resetTimer);
    };
  }, [navigate]);

  const [openSidebar, setOpenSidebar] = useState(window.innerWidth >= 960);

  useEffect(() => {
    const handleResize = () => {
      setOpenSidebar(window.innerWidth >= 960);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 960) {
      setOpenSidebar(false);
    }
  }, [location.pathname]);

  const parseJwt = (token) => {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (e) {
      return null;
    }
  };
  const payload = parseJwt(token);
  const name = payload?.fullName || "";

  const apiBase = "/api/user/notifications";

  const [notifications, setNotifications] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const { unreadCount, fetchUnreadCount } = useNotifications(apiBase);

  const [langAnchor, setLangAnchor] = useState(null);

  const fetchNotifications = () => {
    fetch(`${API_BASE_URL}/api/user/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        const parsed = data.map((n) => ({
          ...n,
          is_read: n.is_read === 1 || n.is_read === true,
        }));
        setNotifications(parsed);
      })
      .catch((err) => console.error("Failed to load notifications", err));
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // const handleLangClick = (e) => setLangAnchor(e.currentTarget);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleProfileClick = (e) => setProfileAnchor(e.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setLangAnchor(null);
    setProfileAnchor(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("isVerified");
    localStorage.removeItem("lastActive");
    localStorage.removeItem("userId");
    navigate("/login");
  };

  const handleOpenModal = async (notif) => {
    setAnchorEl(null);

    if (!notif.is_read) {
      await fetch(`${API_BASE_URL}/api/user/notifications/${notif.id}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)),
      );
      fetchUnreadCount();
    }

    setSelectedNotif(notif);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedNotif(null);
  };

  return (
    <div className="user-layout">
      <Box
        sx={{
          position: window.innerWidth < 960 ? "absolute" : "relative",
          zIndex: 1200,
        }}
      >
        <Navbar
          openSidebar={openSidebar}
          setOpenSidebar={setOpenSidebar}
          isMobile={window.innerWidth < 960}
        />
      </Box>
      <div className="user-content">
        <div className="user-header">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <IconButton
              className="menu-toggle"
              onClick={() => setOpenSidebar(!openSidebar)}
              sx={{ display: window.innerWidth < 960 ? "block" : "none" }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 18,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    height: 2,
                    width: "100%",
                    bgcolor: "black",
                    borderRadius: 2,
                  }}
                />
                <Box
                  sx={{
                    height: 2,
                    width: "100%",
                    bgcolor: "black",
                    borderRadius: 2,
                  }}
                />
                <Box
                  sx={{
                    height: 2,
                    width: "100%",
                    bgcolor: "black",
                    borderRadius: 2,
                  }}
                />
              </Box>
            </IconButton>

            {location.pathname !== "/user/dashboard" && <div />}
            {location.pathname === "/user/dashboard" && (
              <Typography className="welcome" variant="h6" fontWeight="light">
                Welcome,{" "}
                {name && (
                  <Typography component="span" variant="h6" fontWeight="bold">
                    {name}
                  </Typography>
                )}{" "}
                👋
              </Typography>
            )}
          </Box>
          <div className="icon-group">
            {/* <IconButton
              onClick={handleLangClick}
              style={{ background: "#fff" }}
            >
              <LanguageOutlinedIcon className="header-icon" />
            </IconButton>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  borderRadius: "20px",
                },
              }}
            >
              <MenuItem>English</MenuItem>
              <MenuItem>Srpski</MenuItem>
            </Menu> */}

            <IconButton onClick={handleMenuOpen} style={{ background: "#fff" }}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsNoneIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  width: "300px",
                  borderRadius: "20px",
                  position: "relative",
                },
              }}
            >
              <Typography
                sx={{ px: 2, pt: 1, fontWeight: "bold", fontSize: 16 }}
              >
                Notifications
              </Typography>
              <Box sx={{ maxHeight: "250px", overflowY: "auto" }}>
                {notifications.length === 0 ? (
                  <MenuItem disabled>No notifications</MenuItem>
                ) : (
                  notifications.slice(0, 10).map((n) => (
                    <MenuItem
                      key={n.id}
                      onClick={() => handleOpenModal(n)}
                      sx={{
                        bgcolor: !n.is_read ? "#f0f4ff" : "white",
                        borderBottom: "1px solid #eee",
                        whiteSpace: "normal",
                        wordWrap: "break-word",
                      }}
                    >
                      <Box>
                        <Typography
                          fontWeight={!n.is_read ? "bold" : "normal"}
                          fontSize={12}
                        >
                          {n.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(n.created_at)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Box>

              <MenuItem
                onClick={() => {
                  navigate(`/user/notifications`);
                  setAnchorEl(null);
                }}
                sx={{
                  justifyContent: "center",
                  fontWeight: 500,
                  color: "#0b41cd",
                  borderTop: "1px solid #eee",
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  background: "#fff",
                }}
              >
                See all notifications →
              </MenuItem>
            </Menu>

            <IconButton
              onClick={handleProfileClick}
              style={{ background: "#fff" }}
            >
              <Person2OutlinedIcon />
            </IconButton>
            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={handleMenuClose}
              PaperProps={{
                style: {
                  borderRadius: "20px",
                },
              }}
            >
              <MenuItem disabled>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography fontWeight="bold" fontSize={14}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem onClick={() => navigate("/user/settings")}>
                <SettingsOutlinedIcon style={{ marginRight: 10 }} /> Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} style={{ color: "red" }}>
                <LogoutIcon style={{ marginRight: 10 }} />
                Logout
              </MenuItem>
            </Menu>
          </div>
        </div>
        {React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(child, {
                refreshNotifications: fetchNotifications,
                refreshUnread: fetchUnreadCount,
              })
            : child,
        )}
        <Dialog open={modalOpen} onClose={handleModalClose}>
          <DialogTitle>Notification</DialogTitle>
          <DialogContent dividers>
            <Typography gutterBottom>{selectedNotif?.message}</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedNotif?.created_at
                ? formatDate(selectedNotif.created_at)
                : ""}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Close</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default UserLayout;
